import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import { SectionBreakerHorizontal, SubTitle } from '../../../components/Shared'
import { PayoutType } from './payoutTypes'
import { ManagedPaymentMethod } from '../../../util/Managed'
import Radom, { Customer, ExternalAccount, IPriceQuote, PayoutFeeEstimate } from '../../../state/Radom'
import BalanceInput from './BalanceInput'
import { InputLabel } from '../../../components/Input'
import { formatCurrency } from '../../../util/Util'
import Dropdown, { DropdownItem } from '../../../components/Dropdown'
import ExternalAccountSelectorTag from './ExternalAccountSelectorTag'
import SummaryRow from './SummaryRow'
import DetailsRow from './DetailsRow'
import { Currency } from '../../../util/Currencies'
import Spinner from '../../../components/Spinner'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../../util/ExternalAccounts'

interface IPayoutDetailsProps {
  payoutType: PayoutType
  selectedFundingMethod: ManagedPaymentMethod | undefined
  selectedPayoutMethod: ManagedPaymentMethod | undefined | Currency
  selectedCustomer: Customer | null
  onContinue: () => void
  onBack: () => void
  payoutAmount: string
  setPayoutAmount: (amount: string) => void
  chainName: string | undefined
  availableBalance: number
  decimals: number
  // External accounts for both fiat and crypto destination
  externalAccounts: ExternalAccount[]
  selectedExternalAccount?: ExternalAccount
  setSelectedExternalAccount: (account: ExternalAccount) => void
  // Fee state (managed by parent)
  feeEstimate: PayoutFeeEstimate | null
  setFeeEstimate: (fee: PayoutFeeEstimate | null) => void
}

const PayoutDetails = ({
  payoutType,
  selectedFundingMethod,
  selectedPayoutMethod,
  selectedCustomer,
  onContinue,
  onBack,
  payoutAmount,
  setPayoutAmount,
  chainName,
  availableBalance,
  decimals,
  externalAccounts,
  selectedExternalAccount,
  setSelectedExternalAccount,
  feeEstimate,
  setFeeEstimate
}: IPayoutDetailsProps): JSX.Element => {
  const isFiat = payoutType === PayoutType.Fiat

  // Filter external accounts by type: fiat accounts for fiat payouts, crypto for crypto
  const filteredExternalAccounts = useMemo(() =>
    externalAccounts.filter(a => isFiat
      ? isEurIbanExternalAccount(a)
      : a.active && isCryptoExternalAccount(a)),
  [externalAccounts, isFiat]
  )

  const payoutTicker = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker
    : selectedFundingMethod?.ticker || ''

  const [isLoadingFee, setIsLoadingFee] = useState(false)
  const [priceQuote, setPriceQuote] = useState<IPriceQuote | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeExternalAccountDropdown = useRef<() => void>()

  // For fiat payouts, fetch USDC → payout-currency rate; for crypto fetch USDC → USD
  const quoteToCurrency = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker.toUpperCase()
    : 'USD'

  useEffect(() => {
    Radom.getPriceQuotes(['USDC'], [quoteToCurrency])
      .then(quotes => {
        if (quotes.length > 0) {
          setPriceQuote(quotes[0])
        }
      })
      .catch(err => console.error('Failed to fetch price quote', err))
  }, [quoteToCurrency])

  const fetchFeeEstimate = useCallback(async (amount: string) => {
    if (!amount || Number(amount) <= 0) {
      setFeeEstimate(null)
      return
    }

    setIsLoadingFee(true)
    try {
      if (isFiat) {
        // Fiat fee estimation — currency must be UPPERCASE to match backend enum
        const currency = selectedPayoutMethod && 'ticker' in selectedPayoutMethod
          ? selectedPayoutMethod.ticker.toUpperCase()
          : 'EUR'
        const accountType = selectedExternalAccount?.accountOwnerType || selectedCustomer?.accountType || 'individual'

        const fee = await Radom.getPayoutFee({
          currency,
          amount,
          accountType
        })
        setFeeEstimate(fee)
      } else {
        // Crypto fee estimation
        if (selectedFundingMethod) {
          const network = selectedFundingMethod.hostChain?.name || selectedFundingMethod.name
          const fee = await Radom.getPayoutFee({
            currency: 'USD',
            amount,
            network,
            token: selectedFundingMethod.hostChain?.tokenAddress || undefined
          })
          setFeeEstimate(fee)
        }
      }
    } catch (err) {
      console.error('Failed to fetch payout fee', err)
      setFeeEstimate(null)
    } finally {
      setIsLoadingFee(false)
    }
  }, [isFiat, selectedPayoutMethod, selectedExternalAccount, selectedCustomer, selectedFundingMethod, setFeeEstimate])

  // Debounced fee fetch when amount changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      fetchFeeEstimate(payoutAmount)
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [payoutAmount, fetchFeeEstimate])

  const onSelectExternalAccount = useCallback((account: ExternalAccount) => {
    setSelectedExternalAccount(account)
    closeExternalAccountDropdown.current?.()
  }, [setSelectedExternalAccount])

  const formatFeeAmount = (value: number): string => {
    if (value === 0) return '0'
    return Number(value).toFixed(2)
  }

  const formatUsdcTotal = (value: number): string => {
    if (value === 0) return '0'
    return value.toFixed(6)
  }

  const hasAmount = payoutAmount && Number(payoutAmount) > 0

  const minimumFeeApplies = !!feeEstimate?.minimumFeeUsd &&
    feeEstimate.estimatedFeeUsd <= feeEstimate.minimumFeeUsd + 0.000001
  const variableFee = feeEstimate
    ? Math.max(0, feeEstimate.estimatedFeeUsd - feeEstimate.flatFeeUsd)
    : 0

  const recipientReceives = feeEstimate
    ? `${formatFeeAmount(feeEstimate.estimatedRecipientAmount)} ${payoutTicker}`
    : hasAmount
      ? `${payoutAmount} ${payoutTicker}`
      : `0 ${payoutTicker}`

  const totalUsdcRequired = useMemo((): number | null => {
    if (!feeEstimate || !hasAmount) return null
    const fee = Number(feeEstimate.estimatedFeeUsd) || 0
    if (isFiat && priceQuote) {
      const rate = Number(priceQuote.value)
      if (!rate || isNaN(rate)) return null
      const payoutUsdc = Number(payoutAmount) / rate
      return payoutUsdc + fee
    }

    const networkFee = Number(feeEstimate.networkFeeUsd) || 0
    return Number(payoutAmount) + networkFee
  }, [feeEstimate, hasAmount, isFiat, priceQuote, payoutAmount])

  const exceedsAvailableBalance = totalUsdcRequired !== null && totalUsdcRequired - availableBalance > 0.000001
  const cannotContinue = !payoutAmount ||
    +payoutAmount <= 0 ||
    !selectedExternalAccount ||
    isLoadingFee ||
    !feeEstimate ||
    totalUsdcRequired === null ||
    exceedsAvailableBalance

  const exchangeRateDisplay = priceQuote
    ? isFiat
      ? `1 USDC = ${Number(priceQuote.value).toFixed(4)} ${quoteToCurrency}`
      : `1 USDC = $${Number(priceQuote.value).toFixed(3)} USD`
    : isFiat
      ? `1 USDC ≈ 0.9200 ${quoteToCurrency}`
      : '1 USDC ≈ $1.00 USD'

  return (
    <div style={{
      maxWidth: 560,
      width: '100%',
      display: 'flex',
      padding: 28,
      flexDirection: 'column',
      borderRadius: 22,
      border: '1px solid #edf1f7',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.035)',
      gap: 22,
      background: '#fff'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ fontSize: 18.5, fontWeight: 500, lineHeight: '24px', margin: 0 }}>Enter Payout Details</h1>
        <SubTitle style={{ fontSize: 13.5, lineHeight: '20px' }}>Sending to {
          selectedCustomer?.name && selectedCustomer.name.trim().length > 0
            ? selectedCustomer.name
            : selectedCustomer?.email}</SubTitle>
      </div>
      <div style={{
        alignItems: 'center',
        background: '#fbfcff',
        border: '1px solid #edf1f7',
        borderRadius: 16,
        display: 'flex',
        gap: 12,
        justifyContent: 'space-between',
        padding: '12px 14px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: '#7d8492', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Recipient customer</span>
          <span style={{ color: '#20242d', fontSize: 14, fontWeight: 500 }}>
            {selectedCustomer?.name || selectedCustomer?.companyName || selectedCustomer?.email || '-'}
          </span>
        </div>
      </div>
      <SectionBreakerHorizontal />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel style={{ opacity: 1 }}>Payout Amount</InputLabel>
        <BalanceInput
          amount={payoutAmount}
          onAmount={setPayoutAmount}
          availableBalance={availableBalance}
          ticker={isFiat ? payoutTicker : (selectedFundingMethod?.ticker ?? '')}
          showMax={!isFiat} />
        <span style={{
          color: '#808080',
          fontSize: 12.5,
          fontWeight: 400,
          lineHeight: '16px'
        }}>
          Available balance: <span style={{ color: '#20242d', fontWeight: 500 }}>{formatCurrency(availableBalance, '', decimals)}</span> {selectedFundingMethod?.ticker} on {chainName}
        </span>
      </div>

      {/* Destination section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel>{isFiat ? 'Destination Bank Account' : 'Select Destination Address'}</InputLabel>

        {/* Both fiat and crypto use external accounts as destination */}
        <Dropdown
          containerStyle={{ flex: 1 }}
          onCloseFn={(fn) => { closeExternalAccountDropdown.current = fn }}
          selectedContent={
            selectedExternalAccount
              ? <ExternalAccountSelectorTag account={selectedExternalAccount} />
              : <div style={{ display: 'flex', alignItems: 'center', height: 36, opacity: 0.5, fontSize: 12, fontWeight: 500, paddingLeft: 6 }}>
                {isFiat ? 'Select bank account' : 'Select wallet address'}
              </div>
          }
          dropdownContent={
            <div>
              {filteredExternalAccounts.map((account) => (
                <DropdownItem key={account.id} onClick={() => onSelectExternalAccount(account) }>
                  <ExternalAccountSelectorTag account={account} />
                </DropdownItem>
              ))}
              {filteredExternalAccounts.length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#747894', textAlign: 'center' }}>
                  No {isFiat ? 'bank' : 'crypto'} accounts found
                </div>
              )}
            </div>
          }
        />
      </div>

      {/* Fee breakdown section — always show when amount > 0 */}
      <SectionBreakerHorizontal/>
      {hasAmount
        ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <DetailsRow title='Asset Exchange rate' value={exchangeRateDisplay} />
              <DetailsRow title='Estimated arrival' value='Instant' />
              {isLoadingFee
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                    <Spinner />
                    <span style={{ marginLeft: 8, fontSize: 13, color: '#747894' }}>Calculating fees...</span>
                  </div>
                )
                : feeEstimate
                  ? (
                    <>
                      {isFiat
                        ? (
                          <>
                            {variableFee > 0 && !minimumFeeApplies && (
                              <DetailsRow title='Variable Fee' value={`${formatFeeAmount(variableFee)} USDC`} />
                            )}
                            {feeEstimate.flatFeeUsd > 0 && (
                              <DetailsRow title='Flat Fee' value={`${formatFeeAmount(feeEstimate.flatFeeUsd)} USDC`} />
                            )}
                            {feeEstimate.minimumFeeUsd !== undefined && (
                              <DetailsRow title='Minimum Fee Floor' value={`${formatFeeAmount(feeEstimate.minimumFeeUsd)} USDC`} />
                            )}
                            <DetailsRow title='Payout Fee' value={`${formatFeeAmount(feeEstimate.estimatedFeeUsd)} USDC`} />
                          </>
                        )
                        : (
                          <>
                            {feeEstimate.networkFeeUsd !== undefined && feeEstimate.networkFeeUsd !== null && (
                              <DetailsRow title='Network Fee' value={`${formatFeeAmount(feeEstimate.networkFeeUsd)} USDC`} />
                            )}
                          </>
                        )
                      }
                    </>
                  )
                  : (
                    <>
                      {isFiat
                        ? (
                          <>
                            <DetailsRow title='Offramp Fee' value='--' />
                            <DetailsRow title='Payment Rail Fee' value='--' />
                          </>
                        )
                        : <DetailsRow title='Network Fee' value='--' />
                      }
                    </>
                  )
              }
            </div>
            <div>
              <SectionBreakerHorizontal/>
              {isLoadingFee
                ? <SummaryRow title="Recipient Receives" value="Calculating..." />
                : <SummaryRow title="Recipient Receives" value={recipientReceives} />
              }
              {isLoadingFee
                ? <SummaryRow title="Total deducted from balance" value="Calculating..." />
                : totalUsdcRequired !== null
                  ? <SummaryRow title="Total deducted from balance" value={`${formatUsdcTotal(totalUsdcRequired)} USDC`} />
                  : <SummaryRow title="Total deducted from balance" value={`0 USDC`} />
              }
            </div>
          </>
        )
        : <SummaryRow title="Total" value={`0 ${payoutTicker}`} />
      }

      <div style={{ display: 'flex', width: '100%', gap: 12 }}>
        <SecondaryButton onClick={onBack} style={{ width: '100%' }} >
            Back
        </SecondaryButton>

        <PrimaryButton
          onClick={onContinue}
          disabled={cannotContinue}
          style={{ width: '100%' }}
        >
            Continue
        </PrimaryButton>
      </div>
    </div>

  )
}

export default PayoutDetails
